import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Table } from "antd";
import { formatMoney } from "helpers/numberHelper";
import { Payroll } from "types/payrollTypes";
import { fallback } from "utils/stringUtils";
import { RootState } from "redux/store";
import NoDataComponent from "components/Common/NoData";
import { formatToLocaleDate } from "utils/dateUtils";
import CustomButton from "components/Common/ui/CustomButton";
import PayrollStatusBadge from "components/Common/Badge/PayrollStatusBadge";

// Types
type LineType = "base" | "bonus" | "deduction";
type SourceType = "contract" | "leave" | "cnss" | "other" | "amo" | "igr" | "cimr" | "advance";
type StatusType = "paid" | "partially_paid" | "pending" | "failed" | "overdue" | "coming";

interface CompanyFallback {
  name?: string;
  rc?: string;
  ice?: string;
  ifFiscal?: string;
  cnss?: string;
  rib?: string;
  address?: string;
  city?: string;
}

interface Props {
  payroll: Payroll;
  onBack?: () => void;
  onExportPdf?: () => void;
  onPrint?: () => void;
}



const PayrollPdf: React.FC<Props> = ({
  payroll,
  onBack,
  onExportPdf,
  onPrint,
}) => {
  const { t } = useTranslation();



  const status: StatusType = (payroll.computedStatus || payroll.status || "pending") as StatusType;

  // Compute payroll calculations
  const payrollCalculations = useMemo(() => {
    const toNum = (value: number | string | undefined | null): number => Number(value ?? 0);
    const netAmount = toNum(payroll.netAmount);

    const earnings = payroll?.payrollLines?.filter(line =>
      line.lineType === "base" || line.lineType === "bonus"
    ) || [];

    const deductions = payroll?.payrollLines?.filter(line =>
      line.lineType === "deduction"
    ) || [];

    const totalEarnings = earnings.reduce((sum, line) => sum + toNum(line.amount), 0);
    const totalDeductions = deductions.reduce((sum, line) => sum + toNum(line.amount), 0);
    const calculatedGross = payroll.grossAmount;

    return {
      earnings,
      deductions,
      totalEarnings,
      totalDeductions,
      netToPay: netAmount,
      calculatedGross
    };
  }, [payroll]);

  const { earnings, deductions, totalEarnings, totalDeductions, netToPay, calculatedGross } = payrollCalculations;

  // Table columns
  const earningsColumns = [
    {
      title: t("payroll.pdfPayroll.earnings", { defaultValue: "Gains" }),
      dataIndex: "sourceType",
      key: "sourceType",
      render: (sourceType: SourceType) =>
        t(`payroll.sourceType.${sourceType}`)
    },
    {
      title: t("payroll.pdfPayroll.amount", { defaultValue: "Montant" }),
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (amount: number | string) => (
        <strong>{formatMoney(Number(amount ?? 0))}</strong>
      ),
    },
  ];

  const deductionsColumns = [
    {
      title: t("payroll.pdfPayroll.deductions", { defaultValue: "Retenues" }),
      dataIndex: "sourceType",
      key: "sourceType",
      render: (sourceType: SourceType) =>
        t(`payroll.sourceType.${sourceType}`),
    },
    {
      title: t("payroll.pdfPayroll.amount", { defaultValue: "Montant" }),
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (amount: number | string) => formatMoney(Number(amount ?? 0)),
    },
  ];

  const handlePrint = () => onPrint?.() || window.print();

  const renderEmployeeInfo = () => {
    const staff = payroll?.staff;
    const user = staff?.user;

    return (
      <div className="table-responsive">
        <table className="table table-bordered">
          <tbody>
            <tr>
              <th className="w-25">{t("staff.title")}</th>
              <td>{fallback(user?.firstName) + " " + fallback(user?.lastName)}</td>
              <th className="w-25">{t("staff.form.matricule")}</th>
              <td>{fallback(staff?.matricule)}</td>
            </tr>
            <tr>
              <th>{t("staff.form.cinNumber")}</th>
              <td>{fallback(staff?.cinNumber)}</td>
              <th>{t("staff.form.cnssNumber")}</th>
              <td>{fallback(staff?.cnssNumber)}</td>
            </tr>
            <tr>
              <th>{t("staff.form.jobPosition")}</th>
              <td>{staff?.jobPosition?.code ? fallback(t("job_positions." + staff?.jobPosition?.code)) : "_"}</td>
              <th>{t("staff.form.contractType")}</th>
              <td>{fallback(staff?.contract)}</td>
            </tr>
            <tr>
              <th>{t("payroll.pdfPayroll.issued_at")}</th>
              <td colSpan={3}>{formatToLocaleDate(payroll?.createdAt)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderCompanyInfo = () => {
    const instanceInfo = payroll.staff?.instance;
    return (
      <div className="col-md-6">
        <h5 className="mb-3">{fallback(instanceInfo?.establishmentName)}</h5>
        <div className="company-info">
          {/* <div>ICE {fallback(instanceInfo?.ice)}</div> */}
          {/* <div>{t("payroll.pdfPayroll.company_if")} {fallback(instanceInfo?.ifFiscal)}</div> */}
          {/* <div>{t("payroll.pdfPayroll.company_rc")} {fallback(instanceInfo?.rc)}</div> */}
          {/* <div>{t("payroll.pdfPayroll.company_cnss")} {fallback(instanceInfo?.cnss)}</div> */}
          <div>{t("phoneNumber")} {fallback(instanceInfo?.phoneNumber)}</div>
          <div>{t("address")} {fallback(instanceInfo?.address) + ", " + fallback(instanceInfo?.zipCode)}</div>
          <div>{t("academy")} {fallback(instanceInfo?.academy)}</div>
          {/* <div>{t("payroll.pdfPayroll.company_rib")} {fallback(instanceInfo?.rib)}</div> */}
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header Actions */}
        <div className="d-flex justify-content-between align-items-center mb-4 d-print-none">
          <h4 className="mb-0">
            {t("payroll.pdfPayroll.title", { defaultValue: "Bulletin de paie" })}
          </h4>
          <div className="btn-group ">
            <CustomButton
              onClick={handlePrint}
            >
              <i className="fas fa-print me-2"></i>
              {t("payroll.print")}
            </CustomButton>
            <CustomButton
              onClick={onBack}
              variant="outline-light"
            >
              <i className="fas fa-arrow-left me-2"></i>
              {t("back")}
            </CustomButton>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="row mb-4">
              {renderCompanyInfo()}
              <div className="col-md-6 text-md-end">
                <div className="mb-2">
                  <strong>
                    {t("payroll.form.period", { defaultValue: "Période" })}:{" "}
                    {formatToLocaleDate(payroll.startDate)} — {formatToLocaleDate(payroll.endDate)}
                  </strong>
                </div>
                <div>
                  {t("payroll.form.status", { defaultValue: "Statut" })}:{" "}
                  <PayrollStatusBadge status={payroll.computedStatus} />
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {renderEmployeeInfo()}

            <div className="row mt-4">
              <div className="col-lg-8 mb-4">
                <h6 className="mb-3">{t("payroll.pdfPayroll.earnings", { defaultValue: "Gains" })}</h6>
                <Table
                  size="small"
                  dataSource={earnings}
                  columns={earningsColumns}
                  pagination={false}
                  rowKey="id"
                  locale={{ emptyText: <NoDataComponent /> }}
                  summary={() => (
                    <Table.Summary>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <strong>{t("payroll.pdfPayroll.total_earnings", { defaultValue: "Total gains" })}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <strong>{formatMoney(totalEarnings)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              </div>

              <div className="col-lg-4 mb-4">
                <h6 className="mb-3">{t("payroll.pdfPayroll.deductions", { defaultValue: "Retenues" })}</h6>
                <Table
                  size="small"
                  dataSource={deductions}
                  columns={deductionsColumns}
                  pagination={false}
                  rowKey="id"
                  locale={{ emptyText: <NoDataComponent /> }}
                  summary={() => (
                    <Table.Summary>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <strong>{t("payroll.pdfPayroll.total_deductions", { defaultValue: "Total retenues" })}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <strong>{formatMoney(totalDeductions)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              </div>
            </div>

            <hr className="my-4" />

            <div className="row justify-content-end">
              <div className="col-md-6 col-lg-4">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>{t("payroll.form.gross_amount", { defaultValue: "Salaire Brut" })}</th>
                        <td className="text-end">{formatMoney(calculatedGross)}</td>
                      </tr>
                      <tr>
                        <th>{t("payroll.pdfPayroll.total_deductions", { defaultValue: "Total Retenues" })}</th>
                        <td className="text-end">{formatMoney(totalDeductions)}</td>
                      </tr>
                      <tr className="table-success">
                        <th><strong>{t("payroll.pdfPayroll.net_to_pay", { defaultValue: "Net à payer" })}</strong></th>
                        <td className="text-end">
                          <strong>{formatMoney(netToPay)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollPdf;